import React, { memo, useMemo } from "react";
import type { CommercialConfig } from "./commercialConfig";
import {
  formatDemoDocumentNumber,
  getDevise,
  getDisplayAddress,
  getDisplayCompanyName,
  getLogoUrl,
  getSignatureImageUrl,
  getTauxTvaPercent,
} from "./commercialConfig";
import { formatMoney } from "./formatMoney";

export type QuittancePreviewProps = {
  config: CommercialConfig;
};

function QuittancePreviewInner({ config }: QuittancePreviewProps): React.JSX.Element {
  const cur = getDevise(config);
  const title = getDisplayCompanyName(config);
  const addr = getDisplayAddress(config);
  const logo = getLogoUrl(config);
  const sig = getSignatureImageUrl(config);
  const tauxPct = getTauxTvaPercent(config);
  const exo = config.exonereTva === true;

  const demoTtc = useMemo(() => {
    const ht = 250;
    const rate = exo ? 0 : tauxPct / 100;
    return ht + ht * rate;
  }, [exo, tauxPct]);

  const noQuittance = useMemo(
    () => formatDemoDocumentNumber(config, "quittance"),
    [config]
  );
  const noFacture = useMemo(
    () => formatDemoDocumentNumber(config, "facture"),
    [config]
  );

  const bankLines = [
    config.banque?.trim() && `${config.banque}`,
    config.numeroCompte?.trim() && `Compte ${config.numeroCompte}`,
    config.iban?.trim() && `IBAN ${config.iban}`,
    config.mobileMoneyEntreprise?.trim() && `Mobile money : ${config.mobileMoneyEntreprise}`,
  ].filter(Boolean) as string[];

  return (
    <>
      <div className="flex justify-between border-b border-slate-200 pb-4">
        <div>
          {logo ? (
            <img src={logo} alt="" className="mb-2 h-9 max-w-[110px] object-contain" />
          ) : null}
          <p className="text-sm font-bold">{title}</p>
          <p className="text-[10px] text-slate-600">Quittance de paiement</p>
          {addr ? (
            <p className="mt-2 max-w-[240px] whitespace-pre-line text-[9px] text-slate-600">{addr}</p>
          ) : null}
          <p className="mt-1 text-[9px] text-slate-600">
            RCCM : {config.rccm?.trim() || "—"} · NIF : {config.nif?.trim() || "—"}
          </p>
        </div>
        <div className="text-right text-[10px]">
          <p className="font-mono font-semibold">{noQuittance}</p>
          <p>{new Date().toLocaleDateString("fr-FR")}</p>
        </div>
      </div>
      <dl className="mt-6 space-y-2 text-[10px]">
        <div className="flex justify-between border-b border-slate-100 py-1">
          <dt className="text-slate-600">Facture liée</dt>
          <dd className="font-mono">{noFacture}</dd>
        </div>
        <div className="flex justify-between border-b border-slate-100 py-1">
          <dt className="text-slate-600">Montant payé</dt>
          <dd className="font-semibold">{formatMoney(demoTtc, cur)}</dd>
        </div>
        <div className="flex justify-between border-b border-slate-100 py-1">
          <dt className="text-slate-600">Mode de paiement</dt>
          <dd>Virement bancaire</dd>
        </div>
        <div className="flex justify-between border-b border-slate-100 py-1">
          <dt className="text-slate-600">Référence</dt>
          <dd>VIR-2026-889921</dd>
        </div>
        <div className="flex justify-between border-b border-slate-100 py-1">
          <dt className="text-slate-600">Date du paiement</dt>
          <dd>{new Date().toLocaleDateString("fr-FR")}</dd>
        </div>
        <div className="flex justify-between py-1">
          <dt className="text-slate-600">Émis par</dt>
          <dd>{title}</dd>
        </div>
      </dl>
      {bankLines.length > 0 ? (
        <div className="mt-6 border-t border-slate-200 pt-3 text-[10px] text-slate-700">
          <p className="font-semibold text-slate-800">Règlement</p>
          <p className="mt-1 text-[9px] leading-relaxed text-slate-600">{bankLines.join(" · ")}</p>
        </div>
      ) : null}
      {config.conditionsPaiement?.trim() ? (
        <p className="mt-4 whitespace-pre-line text-[9px] text-slate-600">
          <span className="font-semibold text-slate-700">Conditions : </span>
          {config.conditionsPaiement}
        </p>
      ) : null}
      {config.mentionQuittance?.trim() ? (
        <p className="mt-6 whitespace-pre-line text-[9px] text-slate-500">{config.mentionQuittance}</p>
      ) : null}
      {config.piedPageFacture?.trim() ? (
        <p className="mt-4 whitespace-pre-line text-[8px] text-slate-400">{config.piedPageFacture}</p>
      ) : null}
      <div className="mt-8 flex flex-col items-center gap-2">
        {sig ? (
          <img src={sig} alt="" className="h-14 object-contain" />
        ) : null}
        <div className="text-center text-[9px] text-slate-600">
          <p className="font-medium">{config.nomSignataire?.trim() || title}</p>
          {config.fonctionSignataire?.trim() ? <p>{config.fonctionSignataire}</p> : null}
        </div>
      </div>
    </>
  );
}

export const QuittancePreview = memo(QuittancePreviewInner);
